import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { CreditCard, Zap, ShieldCheck, Loader2 } from "lucide-react";
import api from "@/lib/api";

/**
 * Admin-side payment gateway configuration. Dynamic form: reads
 * `required_fields` from each adapter so future gateways just work.
 */
export default function PaymentGatewaysPanel() {
  const [available, setAvailable] = useState([]);
  const [configured, setConfigured] = useState([]);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState("razorpay");
  const [drafts, setDrafts] = useState({}); // { gateway_key: {config, active} }

  const load = async () => {
    try {
      const r = await api.get("/admin/payment-gateways");
      setAvailable(r.data.available || []);
      setConfigured(r.data.configured || []);
      // seed drafts with existing config
      const d = {};
      for (const c of (r.data.configured || [])) d[c.key] = { config: { ...(c.config || {}) }, active: !!c.active };
      setDrafts((prev) => ({ ...d, ...prev }));
    } catch { toast.error("Failed to load payment gateways"); }
  };
  useEffect(() => { load(); }, []);

  const setDraft = (key, patch) => setDrafts((p) => ({ ...p, [key]: { ...(p[key] || { config: {} }), ...patch } }));
  const setField = (key, fname, val) => setDrafts((p) => {
    const cur = p[key] || { config: {}, active: false };
    return { ...p, [key]: { ...cur, config: { ...cur.config, [fname]: val } } };
  });

  const save = async (key, silent = false) => {
    const d = drafts[key] || { config: {}, active: false };
    const meta = available.find((a) => a.key === key);
    for (const f of (meta?.required_fields || [])) {
      if (!(d.config || {})[f]) { if (!silent) toast.error(`${f.replace(/_/g, " ")} required`); return false; }
    }
    setBusy(true);
    try {
      await api.put(`/admin/payment-gateways/${key}`, d);
      if (!silent) toast.success("Saved");
      await load();
      return true;
    } catch (e) { if (!silent) toast.error(e?.response?.data?.detail || "Save failed"); return false; }
    finally { setBusy(false); }
  };

  const test = async (key) => {
    // Auto-save current draft first — this avoids "typed keys → forgot to save → Test uses old keys" confusion.
    const ok = await save(key, true);
    if (!ok) return;
    setBusy(true);
    try {
      const r = await api.post(`/admin/payment-gateways/${key}/test`);
      toast.success(r.data.message || "Test order created");
    } catch (e) { toast.error(e?.response?.data?.detail || "Test failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="dashboard-card p-5 col-span-full lg:col-span-2" data-testid="payment-gateways-panel">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-indigo-100 text-indigo-600 flex items-center justify-center"><CreditCard className="w-5 h-5" /></div>
        <div>
          <h3 className="font-display font-bold text-lg">Payment Gateways</h3>
          <p className="text-xs text-slate-500">Configure keys for public form checkout. One gateway can be &ldquo;active&rdquo; at a time.</p>
        </div>
      </div>

      <div className="space-y-3">
        {available.map((meta) => {
          const cfg = configured.find((c) => c.key === meta.key);
          const draft = drafts[meta.key] || { config: {}, active: false };
          const isOpen = expanded === meta.key;
          return (
            <div key={meta.key} className="border border-slate-200 rounded-xl overflow-hidden" data-testid={`pg-${meta.key}`}>
              <button onClick={() => setExpanded(isOpen ? "" : meta.key)} className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 text-left">
                <div>
                  <div className="font-semibold text-slate-800 flex items-center gap-2">
                    {meta.label}
                    {cfg?.active && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-bold">ACTIVE</span>}
                    {cfg && !cfg.active && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-slate-200 text-slate-600 font-bold">Configured</span>}
                  </div>
                  <div className="text-[11px] text-slate-500">Required: {meta.required_fields.join(", ")}</div>
                </div>
                <span className="text-orange-600 text-sm">{isOpen ? "−" : "+"}</span>
              </button>
              {isOpen && (
                <div className="p-4 space-y-2 border-t border-slate-100">
                  {meta.required_fields.map((f) => (
                    <div key={f}>
                      <label className="text-[10px] font-bold uppercase text-slate-500 block mb-1">{f.replace(/_/g, " ")}</label>
                      <input
                        type={f.includes("secret") ? "password" : "text"}
                        className="input-pill text-sm"
                        placeholder={f === "key_id" ? "rzp_test_… or rzp_live_…" : ""}
                        value={draft.config[f] || ""}
                        onChange={(e) => setField(meta.key, f, e.target.value)}
                        data-testid={`pg-${meta.key}-${f}`}
                      />
                    </div>
                  ))}
                  {/* Optional webhook secret (Razorpay) */}
                  {meta.key === "razorpay" && (
                    <div>
                      <label className="text-[10px] font-bold uppercase text-slate-500 block mb-1">Webhook secret (optional)</label>
                      <input
                        type="password"
                        className="input-pill text-sm"
                        value={draft.config.webhook_secret || ""}
                        onChange={(e) => setField(meta.key, "webhook_secret", e.target.value)}
                        data-testid={`pg-${meta.key}-webhook_secret`}
                      />
                      <div className="text-[10px] text-slate-400 mt-1">Set the same value in Razorpay Dashboard → Webhooks. Endpoint URL: <code className="bg-slate-100 px-1 py-0.5 rounded">{window.location.origin}/api/public/payment-webhook</code></div>
                    </div>
                  )}
                  <label className="flex items-center gap-2 pt-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={!!draft.active} onChange={(e) => setDraft(meta.key, { active: e.target.checked })} data-testid={`pg-${meta.key}-active`} />
                    <span className="font-semibold">Make this the active gateway for all form payments</span>
                  </label>
                  <div className="flex items-center gap-2 pt-2">
                    <button onClick={() => save(meta.key)} disabled={busy} className="btn-primary text-sm py-2 disabled:opacity-50" data-testid={`pg-${meta.key}-save`}>
                      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5" />} Save
                    </button>
                    <button onClick={() => test(meta.key)} disabled={busy} className="btn-ghost text-sm py-2" data-testid={`pg-${meta.key}-test`}>
                      <Zap className="w-3.5 h-3.5" /> Save &amp; Test connectivity
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
